import { ChartFigure } from './ChartFigure';
import { histogramBars } from './geometry';

/** A confidence band for the band-mode histogram (real 4-band breakdown). */
export interface ConfidenceBandDatum {
  /** Range label, e.g. `0.75–0.90`. */
  label: string;
  count: number;
  /** True when the band sits below the acceptance threshold. */
  below: boolean;
}

export interface HistogramProps {
  /** 10 bucket counts, confidence 0.50 → 1.00 (mock demo shape). */
  buckets?: readonly number[];
  /**
   * Real confidence bands from the analytics breakdown (any count). When given,
   * the chart renders one bar per band and colours below-threshold bands warn.
   */
  bands?: readonly ConfidenceBandDatum[];
  summary: string;
  /** Threshold (0–1) marked with the dashed line. Default 0.75. */
  threshold?: number;
}

const BUCKET_LABELS = ['0.50', '0.55', '0.60', '0.65', '0.70', '0.75', '0.80', '0.85', '0.90', '0.95'];

/**
 * §5.6 Confidence distribution histogram. Two shapes: the mock's 10 evenly-spaced
 * `buckets`, or the real analytics `bands` (edges at 0.50/0.75/0.90). Bars below
 * the threshold are `--warn-dot`, above are `--accent` — colour encodes
 * acceptability, not series (§7.3). A hidden table lists every bar for SR users.
 */
export function Histogram({ buckets, bands, summary, threshold = 0.75 }: HistogramProps): React.JSX.Element {
  if (bands !== undefined) return <BandHistogram bands={bands} summary={summary} threshold={threshold} />;
  return <BucketHistogram buckets={buckets ?? []} summary={summary} threshold={threshold} />;
}

function BucketHistogram({
  buckets,
  summary,
  threshold,
}: {
  buckets: readonly number[];
  summary: string;
  threshold: number;
}): React.JSX.Element {
  const bars = histogramBars(buckets);
  // Threshold x in the 0–300 viewBox: bucket 5 (0.75) sits at x≈149 in the mock.
  const thresholdX = 149;
  return (
    <ChartFigure
      summary={summary}
      columns={['Confidence', 'Documents']}
      tableCaption="Confidence distribution"
      rows={buckets.map((count, i) => ({
        label: `${BUCKET_LABELS[i] ?? ''}–${(Number(BUCKET_LABELS[i] ?? '0') + 0.05).toFixed(2)}`,
        values: { Documents: String(Math.round(count)) },
      }))}
    >
      <svg width="100%" height="132" viewBox="0 0 300 132" fill="none" style={{ display: 'block' }} role="img" aria-label={summary}>
        {bars.map((bar, i) => (
          <rect key={i} x={bar.x} y={bar.y} width="20" height={bar.h} rx="2" fill={bar.fill} fillOpacity={bar.opacity} />
        ))}
        <line x1={thresholdX} y1="8" x2={thresholdX} y2="114" stroke="var(--warn-dot)" strokeDasharray="3 3" />
        <text x="12" y="126" fontSize="9.5" fill="var(--t3)">
          0.50
        </text>
        <text x={thresholdX} y="126" fontSize="9.5" fill="var(--warn)" textAnchor="middle">
          {threshold.toFixed(2)}
        </text>
        <text x="288" y="126" fontSize="9.5" fill="var(--t3)" textAnchor="end">
          1.00
        </text>
      </svg>
    </ChartFigure>
  );
}

function BandHistogram({
  bands,
  summary,
  threshold,
}: {
  bands: readonly ConfidenceBandDatum[];
  summary: string;
  threshold: number;
}): React.JSX.Element {
  const n = Math.max(1, bands.length);
  const max = Math.max(...bands.map((b) => b.count), 1);
  const step = 300 / n;
  const barWidth = step * 0.62;
  const firstAbove = bands.findIndex((b) => !b.below);
  const thresholdX = firstAbove <= 0 ? -1 : firstAbove * step;
  const lower = bands[0]?.label.split('–')[0] ?? '0.00';
  const upper = bands[bands.length - 1]?.label.split('–')[1] ?? '1.00';
  return (
    <ChartFigure
      summary={summary}
      columns={['Confidence', 'Documents']}
      tableCaption="Confidence distribution"
      rows={bands.map((b) => ({ label: b.label, values: { Documents: String(Math.round(b.count)) } }))}
    >
      <svg width="100%" height="132" viewBox="0 0 300 132" fill="none" style={{ display: 'block' }} role="img" aria-label={summary}>
        {bands.map((band, i) => {
          const h = Math.max(3, (band.count / max) * 98);
          const x = i * step + (step - barWidth) / 2;
          return (
            <rect
              key={band.label}
              x={x}
              y={112 - h}
              width={barWidth}
              height={h}
              rx="2"
              fill={band.below ? 'var(--warn-dot)' : 'var(--accent)'}
              fillOpacity={band.below ? 0.55 : 0.7}
            />
          );
        })}
        {thresholdX > 0 ? (
          <>
            <line x1={thresholdX} y1="8" x2={thresholdX} y2="114" stroke="var(--warn-dot)" strokeDasharray="3 3" />
            <text x={thresholdX} y="126" fontSize="9.5" fill="var(--warn)" textAnchor="middle">
              {threshold.toFixed(2)}
            </text>
          </>
        ) : null}
        <text x="4" y="126" fontSize="9.5" fill="var(--t3)">
          {lower}
        </text>
        <text x="296" y="126" fontSize="9.5" fill="var(--t3)" textAnchor="end">
          {upper}
        </text>
      </svg>
    </ChartFigure>
  );
}
